import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import PortalLayout from '@/components/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api, useAuth } from '@/App';
import PdfPreviewDialog from '@/components/PdfPreviewDialog';
import NotesThread from '@/components/NotesThread';
import { 
  ArrowLeft, 
  Buildings, 
  Lightning, 
  Drop,
  Clock,
  CheckCircle,
  Warning,
  FileText,
  Upload,
  Download,
  Eye
} from '@phosphor-icons/react';

const PUBLIC_STATUSES = ['New Service Request', 'Quotation Accepted', 'Site Visit Scheduled', 'Query Logged', 'Under Review', 'Approved', 'Completed'];

const PUBLIC_STATUS_COLORS = {
  'New Service Request': 'bg-blue-100 text-blue-700',
  'Quotation Accepted': 'bg-indigo-100 text-indigo-700',
  'Site Visit Scheduled': 'bg-cyan-100 text-cyan-700',
  'Query Logged': 'bg-yellow-100 text-yellow-700',
  'Under Review': 'bg-orange-100 text-orange-700',
  'Approved': 'bg-green-100 text-green-700',
  'Completed': 'bg-emerald-100 text-emerald-700'
};

const PUBLIC_STATUS_DESCRIPTIONS = {
  'New Service Request': 'Your request has been received and is being reviewed.',
  'Quotation Accepted': 'Your quotation has been accepted. A site visit will be scheduled.',
  'Site Visit Scheduled': 'A site visit has been scheduled for your premises.',
  'Query Logged': 'Your query has been formally logged with the municipality.',
  'Under Review': 'Your case is under review by the municipality.',
  'Approved': 'Your case has been approved and resolved successfully.',
  'Completed': 'Your case has been completed.'
};

const PortalCaseDetail = () => {
  const { caseId } = useParams();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const fetchCase = async () => {
    try {
      const { data } = await api.get(`/cases/${caseId}`);
      setCaseData(data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load case');
      navigate('/portal');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, navigate]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post(`/documents/upload?entity_type=case&entity_id=${caseId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded successfully');
      // Refresh case data
      const { data } = await api.get(`/cases/${caseId}`);
      setCaseData(data);
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#0055FF] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </PortalLayout>
    );
  }

  if (!caseData) return null;

  const statusIndex = PUBLIC_STATUSES.indexOf(caseData.public_status);

  return (
    <PortalLayout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to="/portal">
            <Button variant="ghost" size="icon" className="rounded-none">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#0055FF]/10 flex items-center justify-center">
              {caseData.service_type?.includes('Water') ? (
                <Drop className="w-6 h-6 text-[#0055FF]" />
              ) : (
                <Lightning className="w-6 h-6 text-[#0055FF]" />
              )}
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#0A192F]">{caseData.service_type}</h1>
              <p className="text-sm text-[#4B5563]">Case #{caseData.case_id.slice(-8)}</p>
            </div>
          </div>
        </div>

        {/* Status Alert */}
        {caseData.public_status === 'Under Review' && (
          <div className="bg-[#FF4500]/10 border border-[#FF4500]/30 p-4 flex items-start gap-4">
            <Warning className="w-6 h-6 text-[#FF4500] flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-[#0A192F]">Under Review</p>
              <p className="text-sm text-[#4B5563] mt-1">
                {caseData.public_notes || 'Your case is currently under review by the municipality.'}
              </p>
            </div>
          </div>
        )}

        {/* Progress */}
        <Card>
          <CardHeader className="border-b border-[#E5E7EB] pb-4">
            <CardTitle className="text-lg font-semibold text-[#0A192F]">Case Progress</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-6">
              {PUBLIC_STATUSES.map((status, index) => {
                const isComplete = index < statusIndex;
                const isCurrent = index === statusIndex;
                return (
                  <div key={status} className="flex flex-col items-center flex-1">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isComplete ? 'bg-[#10B981] text-white' :
                      isCurrent ? 'bg-[#0055FF] text-white' :
                      'bg-[#E5E7EB] text-[#4B5563]'
                    }`}>
                      {isComplete ? (
                        <CheckCircle className="w-5 h-5" weight="fill" />
                      ) : (
                        <span className="text-sm font-medium">{index + 1}</span>
                      )}
                    </div>
                    <p className={`text-xs mt-2 text-center ${isCurrent ? 'text-[#0055FF] font-medium' : 'text-[#4B5563]'}`}>
                      {status}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="bg-[#F8F9FA] p-4 border-l-4 border-l-[#0055FF]">
              <div className="flex items-center gap-2 mb-1">
                <Badge className={PUBLIC_STATUS_COLORS[caseData.public_status]}>
                  {caseData.public_status}
                </Badge>
              </div>
              <p className="text-sm text-[#4B5563]">
                {PUBLIC_STATUS_DESCRIPTIONS[caseData.public_status]}
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            {/* Conversation thread */}
            <NotesThread
              caseId={caseData.case_id}
              notes={caseData.notes_thread || []}
              legacyNote={caseData.public_notes}
              onPosted={fetchCase}
              onUpdated={fetchCase}
              currentUser={currentUser}
            />

            {/* Meter Info */}
            {caseData.meter && (
              <Card>
                <CardHeader className="border-b border-[#E5E7EB] pb-4">
                  <CardTitle className="text-lg font-semibold text-[#0A192F] flex items-center gap-2">
                    {caseData.meter.meter_type === 'Water' ? (
                      <Drop className="w-5 h-5 text-[#0055FF]" />
                    ) : (
                      <Lightning className="w-5 h-5 text-[#0055FF]" />
                    )}
                    Meter Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-[#4B5563]">Meter Number</p>
                      <p className="font-mono text-[#0A192F]">{caseData.meter.meter_number}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#4B5563]">Type</p>
                      <p className="text-[#0A192F]">{caseData.meter.meter_type}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#4B5563]">Supplier</p>
                      <p className="text-[#0A192F]">{caseData.meter.energy_supplier}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#4B5563]">Status</p>
                      <Badge className={caseData.meter.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100'}>
                        {caseData.meter.status}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Timeline */}
            {caseData.timeline && caseData.timeline.length > 0 && (
              <Card>
                <CardHeader className="border-b border-[#E5E7EB] pb-4">
                  <CardTitle className="text-lg font-semibold text-[#0A192F] flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[#0055FF]" />
                    Case History
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="relative pl-8 space-y-6">
                    {caseData.timeline.map((activity, index) => (
                      <div key={index} className="timeline-item relative">
                        <p className="text-sm font-medium text-[#0A192F]">{activity.action.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                        <p className="text-xs text-[#4B5563] mt-1">
                          {new Date(activity.timestamp).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Company */}
            {caseData.company && (
              <Card>
                <CardHeader className="border-b border-[#E5E7EB] pb-4">
                  <CardTitle className="text-lg font-semibold text-[#0A192F] flex items-center gap-2">
                    <Buildings className="w-5 h-5 text-[#0055FF]" />
                    Company
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="font-medium text-[#0A192F]">{caseData.company.company_name}</p>
                  {caseData.company.physical_address && (
                    <p className="text-sm text-[#4B5563] mt-2">{caseData.company.physical_address}</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Documents */}
            <Card>
              <CardHeader className="border-b border-[#E5E7EB] pb-4">
                <CardTitle className="text-lg font-semibold text-[#0A192F] flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#0055FF]" />
                  Documents
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {caseData.documents && caseData.documents.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {caseData.documents.map((doc) => (
                      <div key={doc.doc_id} className="p-2 border border-[#E5E7EB]" data-testid={`portal-doc-${doc.doc_id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-[#0A192F] truncate font-medium">{doc.original_filename}</p>
                            {doc.document_type && doc.document_type !== doc.original_filename && (
                              <p className="text-xs text-gray-500">{doc.document_type}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-[#0055FF] px-2"
                              onClick={() => setPreview({ url: `/api/documents/${doc.doc_id}/download`, title: doc.document_type || 'Document', filename: doc.original_filename })}
                              data-testid={`portal-view-doc-${doc.doc_id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <a href={`/api/documents/${doc.doc_id}/download`} target="_blank" rel="noopener noreferrer" download={doc.original_filename}>
                              <Button variant="ghost" size="sm" className="text-[#0055FF] px-2" data-testid={`portal-download-doc-${doc.doc_id}`}>
                                <Download className="w-4 h-4" />
                              </Button>
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[#4B5563] mb-4">No documents uploaded yet</p>
                )}

                <label className="block">
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="hidden"
                    data-testid="file-upload"
                  />
                  <Button 
                    variant="outline" 
                    className="w-full rounded-none border-dashed cursor-pointer"
                    disabled={uploading}
                    asChild
                  >
                    <span>
                      <Upload className="w-4 h-4 mr-2" />
                      {uploading ? 'Uploading...' : 'Upload Document'}
                    </span>
                  </Button>
                </label>
              </CardContent>
            </Card>

            {/* Case Info */}
            <Card>
              <CardHeader className="border-b border-[#E5E7EB] pb-4">
                <CardTitle className="text-lg font-semibold text-[#0A192F]">Case Details</CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-3">
                <div>
                  <p className="text-sm text-[#4B5563]">Date Submitted</p>
                  <p className="text-[#0A192F]">{new Date(caseData.date_logged || caseData.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-sm text-[#4B5563]">Service Type</p>
                  <p className="text-[#0A192F]">{caseData.service_type}</p>
                </div>
                <div>
                  <p className="text-sm text-[#4B5563]">Priority</p>
                  <Badge>{caseData.priority}</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <PdfPreviewDialog
        open={!!preview}
        onOpenChange={(open) => !open && setPreview(null)}
        url={preview?.url}
        title={preview?.title}
        filename={preview?.filename}
      />
    </PortalLayout>
  );
};

export default PortalCaseDetail;
